import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Card,
  Checkbox,
  Space,
  Upload,
  message as antMessage,
  Avatar,
  Switch
} from "antd";
import { UploadOutlined, SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { selectCurrentTier } from "../store/tierSlice";
import { doGetTier, doCreateTier, doUpdateTier } from "../store/thunks";
import { clearCurrentTier } from "../store/tierSlice";
import clientApi from "../../clients/store/api";
import axios from "axios";
import Cookies from "universal-cookie";
import { TOKEN_NAME } from "configs/constants";
import "./TierEdit.scss";

const BASE_URL = window.REACT_APP_API_ENDPOINT;

const TierEdit = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const [form] = Form.useForm();
  const currentTier = useSelector(selectCurrentTier);
  const [entitlements, setEntitlements] = useState([]);
  const [selectedEntitlements, setSelectedEntitlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logoFileId, setLogoFileId] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const isEditMode = !!id;

  useEffect(() => {
    // Fetch entitlements list
    const fetchEntitlements = async () => {
      try {
        const data = await clientApi.getEntitlements();
        setEntitlements(data);
      } catch (error) {
        antMessage.error("Failed to load entitlements");
      }
    };

    fetchEntitlements();

    // If editing, fetch tier data
    if (isEditMode) {
      dispatch(doGetTier(id));
    }

    return () => {
      dispatch(clearCurrentTier());
    };
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (currentTier && isEditMode) {
      form.setFieldsValue({
        name: currentTier.name,
        cost: currentTier.cost,
        period: currentTier.period,
        isActive: currentTier.isActive
      });

      if (currentTier.entitlements) {
        const entitlementIds = currentTier.entitlements.map((e) => e.id);
        setSelectedEntitlements(entitlementIds);
      }

      if (currentTier.logo) {
        setLogoFileId(currentTier.logo.id);
        setLogoUrl(currentTier.logo.url);
      }
    }
  }, [currentTier, form, isEditMode]);

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);

      // Get auth token from cookies
      const cookies = new Cookies();
      const token = cookies.get(TOKEN_NAME, { path: "/" });

      const response = await axios.post(`${BASE_URL}/admin/tiers/upload-logo`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        }
      });

      const uploadedFile = response.data;
      setLogoFileId(uploadedFile.id);
      setLogoUrl(uploadedFile.url);
      antMessage.success("Logo uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      antMessage.error("Failed to upload logo");
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const tierData = {
        name: values.name,
        cost: values.cost,
        period: values.period,
        isActive: values.isActive ?? true,
        entitlementIds: selectedEntitlements,
        logoFileId: logoFileId
      };

      if (isEditMode) {
        await dispatch(doUpdateTier({ id, tierData }));
      } else {
        await dispatch(doCreateTier(tierData));
      }

      history.push("/admin/billing/tiers");
    } catch (error) {
      // Error is handled in thunk
    } finally {
      setLoading(false);
    }
  };

  const handleEntitlementToggle = (entitlementId) => {
    setSelectedEntitlements((prev) => {
      if (prev.includes(entitlementId)) {
        return prev.filter((itemId) => itemId !== entitlementId);
      } else {
        return [...prev, entitlementId];
      }
    });
  };

  const handleBack = () => {
    history.push("/admin/billing/tiers");
  };

  return (
    <div className="tier-edit-page">
      <div className="tier-edit-header">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back to Tiers
        </Button>
        <h1>{isEditMode ? "Edit Tier" : "Create New Tier"}</h1>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Card title="Basic Information" className="tier-edit-card">
          <Form.Item
            label="Tier Name"
            name="name"
            rules={[{ required: true, message: "Please enter a tier name" }]}
          >
            <Input size="large" placeholder="e.g., Starter, Business, Enterprise" />
          </Form.Item>

          <Form.Item label="Logo" className="logo-upload-item">
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              {logoUrl && (
                <Avatar
                  src={logoUrl}
                  size={100}
                  shape="square"
                  style={{ marginBottom: 16 }}
                />
              )}
              <Upload
                accept="image/*"
                beforeUpload={handleUpload}
                showUploadList={false}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} loading={uploading}>
                  {logoUrl ? "Change Logo" : "Upload Logo"}
                </Button>
              </Upload>
            </Space>
          </Form.Item>

          <Form.Item
            label="Cost"
            name="cost"
            rules={[{ required: true, message: "Please enter the cost" }]}
          >
            <InputNumber
              size="large"
              style={{ width: "100%" }}
              min={0}
              precision={2}
              step={0.01}
              placeholder="0.00"
              formatter={(value) =>
                value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item
            label="Billing Period"
            name="period"
            rules={[{ required: true, message: "Please select a billing period" }]}
          >
            <Select size="large" placeholder="Select billing period">
              <Select.Option value="daily">Daily</Select.Option>
              <Select.Option value="weekly">Weekly</Select.Option>
              <Select.Option value="monthly">Monthly</Select.Option>
              <Select.Option value="yearly">Yearly</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Status" name="isActive" valuePropName="checked">
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              defaultChecked
            />
          </Form.Item>
        </Card>

        <Card title="Entitlements" className="tier-edit-card">
          <div className="entitlements-list">
            {entitlements
              .filter((ent) => ent.isActive)
              .map((entitlement) => (
                <div key={entitlement.id} className="entitlement-item">
                  <Checkbox
                    checked={selectedEntitlements.includes(entitlement.id)}
                    onChange={() => handleEntitlementToggle(entitlement.id)}
                  >
                    <div className="entitlement-info">
                      <div className="entitlement-header">
                        <strong>{entitlement.name}</strong>
                        <span className="entitlement-id">
                          ID: {entitlement.id.substring(0, 8)}
                        </span>
                      </div>
                      {entitlement.description && (
                        <div className="entitlement-description">
                          {entitlement.description}
                        </div>
                      )}
                    </div>
                  </Checkbox>
                </div>
              ))}
          </div>
        </Card>

        <div className="tier-edit-actions">
          <Button onClick={handleBack} size="large">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<SaveOutlined />}
            size="large"
          >
            {isEditMode ? "Update Tier" : "Create Tier"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default TierEdit;
